// src/App.tsx

import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import {
  // Core pages — eager (auth critical path)
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  EmailVerificationPage,
  MfaSetupPage,
  LogoutPage,
  // Non-page exports — always needed
  GeoFetcher,
  LocationProvider,
  usePreferencesStore,
  PreferenceProvider,
  IdpProvider,
  ReviewPromptProvider,
  PageContent,
  ErrorBoundary,
} from '@digiwedge/tee-time-ui';
import { DomPortalForTour } from '@digiwedge/tour-react';
import Layout from './Layout/Layout.js';
import { RequireMfa } from './RequireMfa.js';
import { useAccountBootstrap } from './hooks/useAccountBootstrap.js';
import { useMfaRequestGuard } from './hooks/useMfaRequestGuard.js';
import { fetchClubById, setTeeTimeBaseUrl } from '@digiwedge/teetime-api-client';
import TourBootstrapper from './TourBootstrapper.js';
import {
  AccountSecurityRedirect,
  BuddiesRedirect,
  LegacyTeeTimesRedirect,
  MfaRedirect,
} from './routeRedirects.js';
import { NotFoundPage } from './NotFoundPage.js';

// ── Lazy route groups ──────────────────────────────────────────────────────
// Each group file re-exports a subset of pages from @digiwedge/tee-time-ui.
// Rollup tree-shakes the barrel so each chunk only includes referenced pages.

// Profile group
const LazyProfilePage = React.lazy(() =>
  import('./pages/profile.js').then((m) => ({ default: m.ProfilePage })),
);
const LazySettingsPage = React.lazy(() =>
  import('./pages/profile.js').then((m) => ({ default: m.SettingsPage })),
);
const LazySocialLinkCallbackPage = React.lazy(() =>
  import('./pages/profile.js').then((m) => ({ default: m.SocialLinkCallbackPage })),
);

// Discovery group
const LazySearchResultsPage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.SearchResultsPage })),
);
const LazyCoursesNearMePage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.CoursesNearMePage })),
);
const LazySpecialsPage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.SpecialsPage })),
);
const LazyContactPage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.ContactPage })),
);
const LazyClubPage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.ClubPage })),
);
const LazyClubTeeTimesPage = React.lazy(() =>
  import('./pages/discovery.js').then((m) => ({ default: m.ClubTeeTimesPage })),
);
// Booking group
const LazyBookingDetailsPage = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.BookingDetailsPage })),
);
const LazyPaymentCallbackPage = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.PaymentCallbackPage })),
);
const LazyPaymentEmbeddedFramePage = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.PaymentEmbeddedFramePage })),
);
const LazyPaymentRedirect = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.PaymentRedirect })),
);
const LazyBookingSuccessPage = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.BookingSuccessPage })),
);
const LazyWalletPage = React.lazy(() =>
  import('./pages/booking.js').then((m) => ({ default: m.WalletPage })),
);

// Features group
const LazyMyWaitlistPage = React.lazy(() =>
  import('./pages/features.js').then((m) => ({ default: m.MyWaitlistPage })),
);
const LazyWaitlistAcceptPage = React.lazy(() =>
  import('./pages/features.js').then((m) => ({ default: m.WaitlistAcceptPage })),
);
const LazyWriteReviewPage = React.lazy(() =>
  import('./pages/features.js').then((m) => ({ default: m.WriteReviewPage })),
);

// Tournaments group
const LazyTournamentListPage = React.lazy(() =>
  import('./pages/tournaments.js').then((m) => ({ default: m.TournamentListPage })),
);
const LazyTournamentLeaderboardPage = React.lazy(() =>
  import('./pages/tournaments.js').then((m) => ({ default: m.TournamentLeaderboardPage })),
);
const LazyTournamentDetailPage = React.lazy(() =>
  import('./pages/tournaments.js').then((m) => ({ default: m.TournamentDetailPage })),
);
const LazyTournamentDrawPage = React.lazy(() =>
  import('./pages/tournaments.js').then((m) => ({ default: m.TournamentDrawPage })),
);
const LazyScorecardPage = React.lazy(() =>
  import('./pages/tournaments.js').then((m) => ({ default: m.ScorecardPage })),
);

// App-local pages (already separate chunks)
const LazyPaymentErrorPage = React.lazy(() => import('./PaymentErrorPage.js'));
const LazySignupPreferences = React.lazy(() => import('./SignupPreferences.js'));
const LazyHealthPage = React.lazy(() => import('./health/HealthPage.js'));
const LazyBuddyInvitePage = React.lazy(() => import('./BuddyInvitePage.js'));

// ── Route handlers (small, keep eager) ─────────────────────────────────────

const BookingRouteHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('id') || searchParams.get('bookingId');
  if (bookingId) {
    return <Navigate to={`/bookings/${encodeURIComponent(bookingId)}`} replace />;
  }
  return <LazyPaymentCallbackPage />;
};

const normalizeParam = (value?: string | null) => (typeof value === 'string' ? value.trim() : '');

const ClubRouteHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawClubCode = normalizeParam(searchParams.get('clubCode') ?? searchParams.get('code'));
  const rawClubId = normalizeParam(searchParams.get('id') ?? searchParams.get('clubId'));
  const dateParam = normalizeParam(searchParams.get('date'));
  const [resolvedCode, setResolvedCode] = useState<string | null>(rawClubCode || null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (rawClubCode) {
      setResolvedCode(rawClubCode);
      setError(null);
      return;
    }
    if (!rawClubId) {
      setResolvedCode(null);
      setError('Missing club identifier.');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const resolveClub = async () => {
      try {
        const club = await fetchClubById(rawClubId, controller.signal);
        if (cancelled) return;
        if (!club?.clubCode) {
          setResolvedCode(null);
          setError('Club not found.');
          return;
        }
        setResolvedCode(club.clubCode);
        setError(null);
      } catch {
        if (!cancelled) {
          setResolvedCode(null);
          setError('Unable to resolve club link.');
        }
      }
    };

    void resolveClub();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [rawClubCode, rawClubId]);

  const target = useMemo(() => {
    if (!resolvedCode) return null;
    const base = `/club/${encodeURIComponent(resolvedCode)}`;
    if (!dateParam) return base;
    return `${base}?date=${encodeURIComponent(dateParam)}`;
  }, [dateParam, resolvedCode]);

  if (target) {
    return <Navigate to={target} replace />;
  }

  if (error) {
    return (
      <PageContent className="pt-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-semibold">Club Not Found</h2>
          <p className="mt-2 text-gray-500">{error}</p>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent className="pt-8">
      <div className="mx-auto max-w-2xl text-center text-gray-500">Loading club details...</div>
    </PageContent>
  );
};

// Ensure Tee Time API client uses an absolute base when provided via env
(() => {
  try {
    const vite = (import.meta as unknown as { env?: { VITE_TEE_TIME_BASE_URL?: string } }).env;
    const proc = (
      globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }
    ).process?.env;
    const base = (vite?.VITE_TEE_TIME_BASE_URL || proc?.['VITE_TEE_TIME_BASE_URL'] || '').trim();
    if (base) setTeeTimeBaseUrl(base);
  } catch {
    /* ignore */
  }
})();

// ── Suspense fallback ──────────────────────────────────────────────────────

const RouteFallback: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="h-32 rounded-lg bg-gray-200 animate-pulse" />
    <div className="h-32 rounded-lg bg-gray-200 animate-pulse" />
  </div>
);

const FullApp: React.FC = () => {
  useAccountBootstrap();
  useMfaRequestGuard();
  const location = useLocation();
  const { preferences, setPreferences } = usePreferencesStore();
  return (
    <IdpProvider>
      <LocationProvider>
        <PreferenceProvider>
          <GeoFetcher>
            <ReviewPromptProvider>
              <Layout>
                <ErrorBoundary
                  key={location.pathname}
                  fallbackTitle="Page unavailable"
                  fallbackDescription="This TeeTime page failed to load. Try again or return home."
                >
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      {/* Core — eager */}
                      <Route path="/mfa" element={<MfaRedirect />} />
                      <Route path="/mfa-setup" element={<MfaSetupPage />} />
                      <Route path="/logout" element={<LogoutPage />} />
                      <Route path="/buddies/invite" element={<LazyBuddyInvitePage />} />
                      <Route element={<RequireMfa />}>
                        <Route path="/" element={<LazySearchResultsPage presentation="home" />} />
                        <Route
                          path="/login"
                          element={<LoginPage appName="TeeTime" oauthAudience="teetime-web" />}
                        />
                        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route
                          path="/signup"
                          element={<RegisterPage oauthPolicyProfile="teetime-web" />}
                        />
                        <Route
                          path="/preferences"
                          element={
                            <LazySignupPreferences
                              preferences={preferences}
                              onSave={setPreferences}
                            />
                          }
                        />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

                        {/* Profile group — lazy */}
                        <Route path="/profile" element={<LazyProfilePage />} />
                        <Route path="/buddies" element={<BuddiesRedirect />} />
                        <Route
                          path="/favourites"
                          element={<Navigate to="/profile?tab=favourites" replace />}
                        />
                        <Route path="/settings" element={<LazySettingsPage />} />
                        <Route path="/account/security" element={<AccountSecurityRedirect />} />
                        <Route
                          path="/auth/social-link/account"
                          element={<LazySocialLinkCallbackPage />}
                        />

                        {/* Discovery group — lazy */}
                        <Route path="/club/:clubCode/teetimes" element={<LazyClubTeeTimesPage />} />
                        <Route path="/search" element={<LazySearchResultsPage />} />
                        <Route path="/courses-near-me" element={<LazyCoursesNearMePage />} />
                        <Route path="/specials" element={<LazySpecialsPage />} />
                        <Route path="/contact" element={<LazyContactPage />} />
                        <Route path="/club" element={<ClubRouteHandler />} />
                        <Route path="/club/:ClubCode" element={<LazyClubPage />} />
                        <Route path="/teetimes/:clubId" element={<LegacyTeeTimesRedirect />} />

                        {/* Booking group — lazy */}
                        <Route path="/booking" element={<BookingRouteHandler />} />
                        <Route
                          path="/booking/:transactionId"
                          element={<LazyPaymentCallbackPage />}
                        />
                        <Route path="/payment" element={<LazyPaymentRedirect />} />
                        <Route path="/payment/callback" element={<LazyPaymentCallbackPage />} />
                        <Route path="/booking-success" element={<LazyBookingSuccessPage />} />
                        <Route
                          path="/bookings"
                          element={<Navigate to="/profile?tab=bookings" replace />}
                        />
                        <Route path="/bookings/:bookingId" element={<LazyBookingDetailsPage />} />

                        {/* Features group — lazy */}
                        <Route path="/waitlist" element={<LazyMyWaitlistPage />} />
                        <Route path="/waitlist/accept" element={<LazyWaitlistAcceptPage />} />
                        <Route path="/review" element={<LazyWriteReviewPage />} />

                        {/* Tournaments group — lazy */}
                        <Route path="/tournaments" element={<LazyTournamentListPage />} />
                        <Route path="/tournament" element={<LazyTournamentLeaderboardPage />} />
                        <Route path="/tournament/detail" element={<LazyTournamentDetailPage />} />
                        <Route path="/tournament/draw" element={<LazyTournamentDrawPage />} />
                        <Route path="/scorecard" element={<LazyScorecardPage />} />
                        <Route path="/scorecard/:scorecardId" element={<LazyScorecardPage />} />

                        {/* App-local — lazy */}
                        <Route path="/payment-error" element={<LazyPaymentErrorPage />} />
                        <Route path="/wallet" element={<LazyWalletPage />} />
                        <Route path="/health" element={<LazyHealthPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Layout>
              <DomPortalForTour />
              <TourBootstrapper />
            </ReviewPromptProvider>
          </GeoFetcher>
        </PreferenceProvider>
      </LocationProvider>
    </IdpProvider>
  );
};

// ── App ────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const location = useLocation();
  if (location.pathname === '/payment/embedded') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/payment/embedded" element={<LazyPaymentEmbeddedFramePage />} />
        </Routes>
      </Suspense>
    );
  }

  return <FullApp />;
};

export default App;
